// web/src/billing/api.ts
//
// Thin wrappers around the four billing endpoints. The useSubscription
// query is the single source of truth for the UI's view of subscription
// state — TrialBanner, PastDueBanner, BillingSettings, AskTapelensButton's
// paywall decision all read from it.
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "@/api/client";

export interface SubscriptionState {
  tier: "free" | "pro" | "analyst";
  status:
    | "none"
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "incomplete"
    | "unpaid";
  trial_ends_at: string | null;
  current_period_end: string | null;
  billing_cadence: "monthly" | "annual" | null;
  has_subscription: boolean;
}

export const SUBSCRIPTION_QUERY_KEY = ["billing", "subscription"] as const;

export function useSubscription() {
  return useQuery<SubscriptionState>({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: () => apiFetch<SubscriptionState>("/api/v1/billing/subscription"),
    refetchOnWindowFocus: true,
  });
}

export interface CheckoutSessionRequest {
  plan: "pro" | "analyst";
  cadence: "monthly" | "annual";
}

export function useCheckoutSession() {
  return useMutation({
    mutationFn: (body: CheckoutSessionRequest) =>
      apiFetch<{ url: string }>("/api/v1/billing/checkout-session", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

export function usePortalSession() {
  return useMutation({
    mutationFn: (body: { return_url?: string } = {}) =>
      apiFetch<{ url: string }>("/api/v1/billing/portal-session", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

const POLL_INTERVAL_MS = 2_000;
const POLL_TOTAL_MS = 30_000;

/**
 * Mounted by BillingSettings. When the URL carries `?checkout=success`
 * (the redirect target after Stripe Checkout), we don't know how long
 * Stripe will take to fire the webhook that flips us from free → pro.
 * Aggressively re-poll the subscription query for 30s so the UI
 * activates as soon as the webhook lands.
 *
 * Spec §4.E "Race after Checkout return".
 */
export function useCheckoutSuccessPolling(): void {
  const [searchParams] = useSearchParams();
  const isCheckoutReturn = searchParams.get("checkout") === "success";
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isCheckoutReturn) return;
    queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    }, POLL_INTERVAL_MS);
    const stop = setTimeout(() => clearInterval(interval), POLL_TOTAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [isCheckoutReturn, queryClient]);
}
